import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { type CampaignReportCounts } from '@/api/routes/partners/partners.campaigns.api';
import { formatCount, formatCtr } from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  totals: CampaignReportCounts;
  /** The offer collects contacts: the last indicator counts them instead of dismissals. */
  collectsLeads: boolean;
  /** The offer has a coupon (or had copies in the window): adds "Cupons copiados". */
  showsCouponCopies: boolean;
}

/** P19 totals of the window. Totals are never hidden: they describe the whole campaign. */
const ReportIndicators = ({ totals, collectsLeads, showsCouponCopies }: Props) => {
  const t = useTranslate('domain.results.components.ReportIndicators');

  const items = [
    { label: t('impressions'), value: formatCount(totals.impressions) },
    { label: t('reach'), value: formatCount(totals.reach), hint: t('reachHint') },
    { label: t('clicks'), value: formatCount(totals.clicks) },
    { label: t('ctr'), value: formatCtr(totals.ctr) },
    collectsLeads
      ? { label: t('leads'), value: formatCount(totals.leads), hint: t('leadsHint') }
      : { label: t('dismisses'), value: formatCount(totals.dismisses), hint: t('dismissesHint') },
    ...(showsCouponCopies
      ? [
          {
            label: t('couponCopies'),
            value: formatCount(totals.couponCopies),
            hint: t('couponCopiesHint'),
          },
        ]
      : []),
  ];

  return (
    <Box sx={sx.grid}>
      {items.map((item) => (
        <Paper key={item.label} variant="outlined" sx={sx.card}>
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
          <Typography variant="h3" component="p">
            {item.value}
          </Typography>
          {item.hint ? (
            <Typography variant="caption" color="text.secondary">
              {item.hint}
            </Typography>
          ) : null}
        </Paper>
      ))}
    </Box>
  );
};

export default ReportIndicators;

const sx = {
  grid: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(auto-fit, minmax(140px, 1fr))',
    },
  },
  card: {
    borderRadius: radiusTokens.xxl,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
};
